import { LitElement } from '~/lib/bn-lit-element';
import { LitSirenEntityMixin } from '../../utility/lit-siren-entity-mixin';
import { css, html, renderIf, SirenActionMixin } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class ImportMappingTarget extends SirenActionMixin(LitSirenEntityMixin(LitElement)) {
  static get properties() {
    return {
      recordPreview: { type: Object },
      readonly: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .header {
        min-height: 1.5em;
        margin: 0.5em 0.5em 0 0.5em;
        padding: 0.5em;
        color: var(--white-base);
        background-color: var(--blue-50);
        transition: min-height 0.2s ease-in;
        overflow: auto;
        overflow-wrap: break-word;
      }
      .header-placeholder {
        background-color: var(--blue-10);
        outline: 2px dotted var(--blue-60);
        color: var(--blue-60);
        font-style: italic;
      }
      .target {
        margin-top: 0.5em;
        padding: 0.3em 0.3em 0.6em 0.3em;
        color: var(--blue-60);
        background-color: var(--blue-20);
        font-weight: bold;
        font-size: smaller;
        display: flex;
        flex-direction: column;
        height: auto;
      }
      .close-button {
        display: flex;
        float: right;
        font-size: 16px;
        height: 18px;
        align-items: center;
        padding: 0.25em 0.5em
      }
      .preview {
        font-weight: normal;
        font-style: italic;
      }
      .drop-target {
        outline-color: transparent;
        background-color: var(--blue-50);
        color: var(--white-base);
        opacity: 0.5;
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html``;
    }
    const recordPreview = this.recordPreview || {};
    const { target: targetId, name, headers } = this.entity.properties;
    const placeholderText = this.entity.hasClass('required') && headers.length === 0 ? 'Required field' : '';
    return html`
      <span
        class="target"
        id="${targetId}"
        @drop="${this._onDrop}"
        @dragover="${this._onDragOver}"
        @dragenter="${this._onDragEnter}"
        @dragleave="${this._onDragLeave}"
      >
        <div class="customer-name">
          ${name}
          ${renderIf(!this.readonly && this.entity.getAction('remove-tag'), () => html`
            <button class="button button--tertiary close-button" @click="${() => this._removeTag()}">
              <bn-icon name="far fa-times"></bn-icon>
            </button>
          `)}
        </div>
        ${renderIf(headers.length > 0, () => html`
          ${headers.map((header) => html`
            <span class="header">
              ${header}
              ${renderIf(!this.readonly, () => html`
                <button class="button button--contained close-button" @click="${() => this._removeHeader(targetId, header)}">
                  <bn-icon name="far fa-times"></bn-icon>
                </button>
              `)}
              ${renderIf(recordPreview[header], () => html`
                <div class="preview">${recordPreview[header]}</div>
              `)}
            </span>
          `)}
        `)}
        ${renderIf(!this.readonly && (headers.length === 0 || this.entity.hasClass('multi-select-target')), () => html`
          <span class="header header-placeholder">${placeholderText}</span>
        `)}
      </span>
    `;
  }

  async _onDrop(e) {
    if (this.readonly) {
      return;
    }
    e.preventDefault();
    const header = e.dataTransfer.getData('text/plain');
    const selectorEntity = await this._getSelectorEntity();
    if (!selectorEntity) {
      return;
    }
    selectorEntity.hasClass('multi-selector')
      ? await this._addMultiselectHeader({ selectorEntity, header })
      : await this._assignHeader({ selectorEntity, header });

    const dropTarget = this._getDropTarget();
    dropTarget && dropTarget.classList.remove('drop-target');
  }

  _onDragOver(e) {
    if (this.readonly) {
      return;
    }
    e.preventDefault();
  }

  _onDragEnter(e) {
    if (e.target.className === 'target') {
      const dropTarget = this._getDropTarget();
      dropTarget && dropTarget.classList.add('drop-target');
    }
  }

  _onDragLeave(e) {
    if (e.target.classList.contains('target') && !e.target.contains(e.relatedTarget)) {
      const dropTarget = this._getDropTarget();
      dropTarget && dropTarget.classList.remove('drop-target');
    }
  }

  _getDropTarget() {
    return this.shadowRoot.querySelector('.header-placeholder')
      || this.shadowRoot.querySelector('.header');
  }

  async _getSelectorEntity() {
    const beginAssignHeadersAction = this.entity.getActionByName('begin-assign-headers');
    const actionResult = await this.submitAction(beginAssignHeadersAction);
    return actionResult.entity;
  }

  async _assignHeader({ selectorEntity, header }) {
    const option = selectorEntity.entities.find((entity) => entity.properties.value === header);
    if (option) {
      const res = await this.submitAction(option.getActionByName('assign-headers'));
      this.dispatchEvent(new CustomEvent('change', { detail: { entity: res.entity }, bubbles: true, composed: true }));
    }
  }

  async _addMultiselectHeader({ selectorEntity, header }) {
    const option = selectorEntity.entities.find((entity) => entity.properties.value === header);
    if (option) {
      const selectAction = option.getActionByName('select');
      if (selectAction) {
        const { entity: updatedSelector } = await this.submitAction(selectAction);
        const res = await this.submitAction(updatedSelector.getActionByName('assign-headers'));
        this.dispatchEvent(new CustomEvent('change', { detail: { entity: res.entity }, bubbles: true, composed: true }));
      }
    }
  }

  async _removeTag() {
    const res = await this.submitAction(this.entity.getAction('remove-tag'));
    this.dispatchEvent(new CustomEvent('change', { detail: { entity: res.entity }, bubbles: true, composed: true }));
  }

  async _removeHeader(target, header) {
    const selectorEntity = await this._getSelectorEntity(target);
    if (!selectorEntity) {
      return;
    }
    if (selectorEntity.hasClass('multi-selector')) {
      return this._removeMultiselectHeader({ selectorEntity, header });
    }
    return this._assignHeader({ selectorEntity, header: '' });
  }

  async _removeMultiselectHeader({ selectorEntity, header }) {
    const option = selectorEntity.entities.find((entity) => entity.properties.value === header);
    if (option) {
      const deselectAction = option.getActionByName('deselect');
      if (deselectAction) {
        const { entity: updatedSelector } = await this.submitAction(deselectAction);
        const res = await this.submitAction(updatedSelector.getActionByName('assign-headers'));
        this.dispatchEvent(new CustomEvent('change', { detail: { entity: res.entity }, bubbles: true, composed: true }));
      }
    }
  }
}

window.customElements.define('import-mapping-target', ImportMappingTarget);
